import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import {
  Button,
  Card,
  Input,
  Space,
  Table,
  message,
  Spin,
  Modal,
  Radio
} from 'antd'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'
import styled from 'styled-components'

import { Layout } from 'admin/lib/Layouts'
import { useApi, Queries } from 'admin/lib/Api'
import * as ROUTES from 'admin/routes'

if (pdfjs && pdfjs.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs'
}

interface PageRange {
  id: string
  startPage: number
  endPage: number
}

export function PdfSplitter() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{
    accountId: string
    projectId: string
    moduleName: string
    recordId: string
    attachmentIndex: string
  }>()
  const api = useApi()

  const attachmentRotations = (location.state as any)?.attachmentRotations || []
  // round rotations to nearest 90 degrees for display
  const roundedRotations = attachmentRotations.map((angle: number) => {
    const normalizedAngle = ((angle % 360) + 360) % 360
    if (normalizedAngle < 45 || normalizedAngle >= 315) return 0
    if (normalizedAngle >= 45 && normalizedAngle < 135) return 90
    if (normalizedAngle >= 135 && normalizedAngle < 225) return 180
    return 270
  })

  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [pageRanges, setPageRanges] = useState<PageRange[]>([])
  const [loading, setLoading] = useState(false)
  const [previewPage, setPreviewPage] = useState(1)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [splitMode, setSplitMode] = useState<'pageNumbers' | 'everyN'>(
    'pageNumbers'
  )
  const [pageNumbersInput, setPageNumbersInput] = useState<string>('')
  const [everyNPages, setEveryNPages] = useState<number>(1)
  const [magnifierVisible, setMagnifierVisible] = useState(false)
  const [canvasPosition, setCanvasPosition] = useState({ x: 0, y: 0 })
  const [viewportPosition, setViewportPosition] = useState({ x: 0, y: 0 })
  const [pdfWidth, setPdfWidth] = useState(450)
  const canvasDataUrlRef = useRef<string>('')
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const pdfPageRef = useRef<HTMLDivElement>(null)
  const [actualPdfDimensions, setActualPdfDimensions] = useState({
    width: 0,
    height: 0
  })
  const [pdfMetadataRotations, setPdfMetadataRotations] = useState<number[]>([])

  const MAGNIFIER_SIZE = 160
  const MAGNIFIER_RADIUS = MAGNIFIER_SIZE / 2
  const MAGNIFIER_ZOOM = 2

  // Load record to get attachment
  const record = Queries.useRecord(
    params.accountId,
    params.projectId,
    params.moduleName,
    params.recordId
  )

  // Auto-load PDF from record attachment
  useEffect(() => {
    if (record.data && params.attachmentIndex) {
      const attachmentIdx = parseInt(params.attachmentIndex) - 1
      const attachment = record.data.attachments?.[attachmentIdx]

      if (attachment?.readUri) {
        // Just set the URL, let react-pdf handle the download
        setPdfUrl(attachment.readUri || '')
        setPageRanges([])
        message.success('Record PDF loaded successfully')
      }
    }
  }, [record.data, params.attachmentIndex])

  const DocumentOptions = useMemo(
    () => ({
      wasmUrl: '/assets/wasm/'
    }),
    []
  )

  const handlePDFLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages)
      // Initialize metadata rotations array for all pages
      setPdfMetadataRotations(new Array(numPages).fill(0))
      // Capture actual rendered dimensions after load
      setTimeout(() => {
        if (pdfPageRef.current) {
          const canvas = pdfPageRef.current.querySelector('canvas')
          if (canvas) {
            setActualPdfDimensions({
              width: canvas.offsetWidth,
              height: canvas.offsetHeight
            })
          }
        }
      }, 100)
    },
    []
  )

  // Calculate PDF width based on container size
  useEffect(() => {
    const updateWidth = () => {
      if (previewContainerRef.current) {
        const containerWidth = previewContainerRef.current.offsetWidth
        // Account for navigation buttons (2 x 40px) and gaps (2 x 16px)
        const availableWidth = Math.max(containerWidth - 112, 300)
        setPdfWidth(availableWidth)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [pdfUrl])

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere with arrow keys when user is typing in an input
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setPreviewPage(p => Math.max(1, p - 1))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setPreviewPage(p => Math.min(numPages, p + 1))
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [numPages])

  const validateRanges = useCallback((): boolean => {
    if (pageRanges.length === 0) {
      message.error('Please generate page ranges first')
      return false
    }

    return true
  }, [pageRanges])

  // Handle page render to capture PDF metadata rotation
  const handlePageRenderSuccess = useCallback(
    (pageNumber: number, pageObject: any) => {
      setPdfMetadataRotations(prevRotations => {
        const newRotations = [...prevRotations]
        newRotations[pageNumber - 1] = pageObject.rotate || 0
        return newRotations
      })
    },
    []
  )

  // Calculate the combined rotation for display
  const calculateDisplayRotation = useCallback(
    (pageNumber: number): number => {
      const ocrRotation = roundedRotations[pageNumber - 1] || 0
      const metadataRotation = pdfMetadataRotations[pageNumber - 1] || 0

      // Combine rotations: subtract OCR rotation and add metadata rotation
      // (matching the logic from AttachmentView/PDFViewer)
      return metadataRotation - ocrRotation
    },
    [roundedRotations, pdfMetadataRotations]
  )

  // Generate page ranges based on split mode
  const generatePageRanges = useCallback(() => {
    if (numPages === 0) {
      message.error('Please wait for the PDF to load')
      return
    }

    const ranges: PageRange[] = []

    if (splitMode === 'pageNumbers') {
      // Parse comma or space-separated page numbers
      const pageNumbers = pageNumbersInput
        .split(/[,\s]+/)
        .map(p => parseInt(p.trim()))
        .filter(p => !isNaN(p) && p > 0 && p <= numPages)
        .sort((a, b) => a - b)

      if (pageNumbers.length === 0) {
        message.error('Please enter valid page numbers')
        return
      }

      // Create ranges by splitting after each specified page
      let startPage = 1
      pageNumbers.forEach((pageNum, index) => {
        if (pageNum >= startPage) {
          ranges.push({
            id: `range-${index}`,
            startPage,
            endPage: pageNum
          })
          startPage = pageNum + 1
        }
      })

      // Add final range if there are remaining pages
      if (startPage <= numPages) {
        ranges.push({
          id: `range-${pageNumbers.length}`,
          startPage,
          endPage: numPages
        })
      }
    } else if (splitMode === 'everyN') {
      if (everyNPages < 1) {
        message.error('Number of pages must be at least 1')
        return
      }

      // Create ranges of N pages each
      let startPage = 1
      let rangeIndex = 0
      while (startPage <= numPages) {
        const endPage = Math.min(startPage + everyNPages - 1, numPages)
        ranges.push({
          id: `range-${rangeIndex}`,
          startPage,
          endPage
        })
        startPage = endPage + 1
        rangeIndex++
      }
    }

    setPageRanges(ranges)
    message.success(`Generated ${ranges.length} page range(s)`)
  }, [numPages, splitMode, pageNumbersInput, everyNPages])

  const handleSplitClick = useCallback(() => {
    if (!validateRanges()) {
      return
    }
    setConfirmModalOpen(true)
  }, [validateRanges])

  const handleConfirmSplit = useCallback(async () => {
    setLoading(true)

    try {
      if (!api) {
        message.error('API not available')
        return
      }

      if (
        !params.accountId ||
        !params.moduleName ||
        !params.projectId ||
        !params.recordId
      ) {
        message.error('Missing required parameters')
        return
      }

      const ranges = pageRanges.map(range => {
        if (range.startPage === range.endPage) {
          return String(range.startPage)
        }
        return `${range.startPage}-${range.endPage}`
      })

      const [data, status] = await api.postSplitRecordWithRanges(
        params.accountId,
        params.moduleName,
        params.projectId,
        params.recordId,
        ranges
      )

      if (status === 200 || status === 204) {
        message.success(`Successfully split record into ${data} new records.`)
        setConfirmModalOpen(false)

        // Attempt to close the window with workaround for browser security restrictions
        setTimeout(() => {
          // Try the workaround pattern for browser security
          window.opener = null
          window.open('', '_self')
          window.close()

          // If we reach here, window.close() was blocked by browser security.
          // Give browser a moment to process, then navigate to INTRAY as fallback
          requestAnimationFrame(() => {
            navigate(ROUTES.INTRAY)
          })
        }, 2000)
      } else {
        message.error('Failed to split record')
      }
    } catch (error) {
      message.error('Failed to split record')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [api, params, pageRanges, navigate])

  const columns = [
    {
      title: 'Start Page',
      dataIndex: 'startPage',
      key: 'startPage',
      width: 120
    },
    {
      title: 'End Page',
      dataIndex: 'endPage',
      key: 'endPage',
      width: 120
    },
    {
      title: 'Pages',
      key: 'pageCount',
      width: 80,
      render: (_: any, record: PageRange) => (
        <span>{record.endPage - record.startPage + 1}</span>
      )
    }
  ]

  return (
    <Layout header={null}>
      <Container>
        <ContentWrapper>
          <LeftPanel>
            <h1>Split PDF Record</h1>
            <p>Define page ranges to split this record into multiple records</p>
            <Card title="Split Settings">
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="middle"
              >
                <Radio.Group
                  value={splitMode}
                  onChange={e => setSplitMode(e.target.value)}
                  disabled={!pdfUrl}
                >
                  <Space direction="vertical">
                    <Radio value="pageNumbers">
                      Split <b>after</b> the following page numbers
                    </Radio>
                    <Radio value="everyN">Split by every "n" pages</Radio>
                  </Space>
                </Radio.Group>

                {splitMode === 'pageNumbers' && (
                  <Input
                    placeholder="Page numbers to split at (e.g., 3,6,9 or 3 6 9)"
                    value={pageNumbersInput}
                    onChange={e => setPageNumbersInput(e.target.value)}
                    disabled={!pdfUrl}
                  />
                )}

                {splitMode === 'everyN' && (
                  <Input
                    type="number"
                    placeholder="Number of pages"
                    min={1}
                    value={everyNPages}
                    onChange={e =>
                      setEveryNPages(parseInt(e.target.value) || 1)
                    }
                    disabled={!pdfUrl}
                  />
                )}

                <Button
                  type="dashed"
                  onClick={generatePageRanges}
                  block
                  disabled={!pdfUrl}
                >
                  Generate Page Ranges
                </Button>

                <Table
                  dataSource={pageRanges}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'No page ranges defined' }}
                />

                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleSplitClick}
                  block
                  disabled={!pdfUrl || pageRanges.length === 0}
                >
                  Split Record
                </Button>

                <Button block onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Space>
            </Card>
          </LeftPanel>

          <RightPanel>
            <Card title="PDF Preview" size="small">
              {pdfUrl ? (
                <PreviewContainer ref={previewContainerRef}>
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={handlePDFLoadSuccess}
                    options={DocumentOptions}
                    loading={<Spin />}
                    scale={1.5}
                  >
                    {numPages > 0 && (
                      <NavigationLayout>
                        <Button
                          icon={<LeftOutlined />}
                          onClick={() =>
                            setPreviewPage(p => Math.max(1, p - 1))
                          }
                          disabled={previewPage <= 1}
                          size="large"
                        />
                        <div>
                          <PdfWrapper
                            ref={pdfPageRef}
                            onMouseMove={e => {
                              if (pdfPageRef.current) {
                                const canvas =
                                  pdfPageRef.current.querySelector('canvas')
                                if (canvas) {
                                  const canvasRect =
                                    canvas.getBoundingClientRect()
                                  setViewportPosition({
                                    x: e.clientX,
                                    y: e.clientY
                                  })
                                  setCanvasPosition({
                                    x: e.clientX - canvasRect.left,
                                    y: e.clientY - canvasRect.top
                                  })
                                }
                              }
                            }}
                            onMouseEnter={() => {
                              setMagnifierVisible(true)
                              // Capture canvas snapshot with slight delay to ensure it's rendered
                              setTimeout(() => {
                                const canvas =
                                  pdfPageRef.current?.querySelector('canvas')
                                if (canvas) {
                                  canvasDataUrlRef.current = canvas.toDataURL()
                                }
                              }, 50)
                            }}
                            onMouseLeave={() => setMagnifierVisible(false)}
                          >
                            <Page
                              pageNumber={previewPage}
                              width={pdfWidth}
                              rotate={calculateDisplayRotation(previewPage)}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              onRenderSuccess={pageObject =>
                                handlePageRenderSuccess(previewPage, pageObject)
                              }
                            />
                            {magnifierVisible &&
                              actualPdfDimensions.width > 0 &&
                              canvasDataUrlRef.current && (
                                <Magnifier
                                  style={{
                                    left: `${viewportPosition.x}px`,
                                    top: `${viewportPosition.y}px`,
                                    backgroundImage: `url(${canvasDataUrlRef.current})`,
                                    backgroundSize: `${actualPdfDimensions.width * MAGNIFIER_ZOOM}px ${actualPdfDimensions.height * MAGNIFIER_ZOOM}px`,
                                    backgroundPosition: `${MAGNIFIER_RADIUS - canvasPosition.x * MAGNIFIER_ZOOM}px ${MAGNIFIER_RADIUS - canvasPosition.y * MAGNIFIER_ZOOM}px`,
                                    backgroundRepeat: 'no-repeat'
                                  }}
                                />
                              )}
                          </PdfWrapper>
                          <PageIndicator>
                            Page {previewPage} of {numPages}
                          </PageIndicator>
                        </div>
                        <Button
                          icon={<RightOutlined />}
                          onClick={() =>
                            setPreviewPage(p => Math.min(numPages, p + 1))
                          }
                          disabled={previewPage >= numPages}
                          size="large"
                        />
                      </NavigationLayout>
                    )}
                  </Document>
                </PreviewContainer>
              ) : (
                <EmptyState>Loading PDF...</EmptyState>
              )}
            </Card>
          </RightPanel>
        </ContentWrapper>

        <Modal
          title="Confirm Split"
          open={confirmModalOpen}
          onOk={handleConfirmSplit}
          confirmLoading={loading}
          onCancel={() => setConfirmModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setConfirmModalOpen(false)}>
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              loading={loading}
              onClick={handleConfirmSplit}
            >
              OK
            </Button>
          ]}
        >
          <p>
            This will split the record into <strong>{pageRanges.length}</strong>{' '}
            new record(s) based on the defined page ranges:
          </p>
          <ul>
            {pageRanges.map((range, index) => (
              <li key={range.id}>
                Record {index + 1}: Page
                {range.startPage !== range.endPage ? 's' : ''} {range.startPage}
                {range.startPage !== range.endPage && `-${range.endPage}`}
              </li>
            ))}
          </ul>
          <p>
            The original record will be <b>voided</b>. Continue?
          </p>
        </Modal>
      </Container>
    </Layout>
  )
}

const Container = styled.div`
  padding: 24px 16px 16px 16px;
  max-width: 1400px;
  margin: 0 auto;

  h1 {
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 16px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`

const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
`

const RightPanel = styled.div`
  flex: 0 0 800px;
  position: sticky;
  top: 24px;
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1200px) {
    flex: 1;
    width: 100%;
    position: relative;
    top: 0;
  }
`

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  .react-pdf__Document {
    display: flex;
    justify-content: center;
  }

  .react-pdf__Page {
    border: 1px solid #ddd;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-height: calc(100vh - 200px);
    width: auto !important;
    height: auto !important;
  }

  .react-pdf__Page canvas {
    max-height: calc(100vh - 200px);
    width: auto !important;
    height: auto !important;
  }
`

const NavigationLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  width: 100%;
`

const PageIndicator = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 14px;
`

const PdfWrapper = styled.div`
  position: relative;
  cursor: crosshair;
`

const Magnifier = styled.div`
  position: fixed;
  width: 150px;
  height: 150px;
  border: 3px solid #1890ff;
  border-radius: 50%;
  pointer-events: none;
  overflow: hidden;
  transform: translate(-50%, -50%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  background: white;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
`
